/** Inline tag editor (design spec §9): free-text field + the workflow's
 * existing tags as one-click options; picking a tag already held by
 * another version shows a move note before applying. Owns its own
 * trigger + the design system's Radix-backed Popover (same primitive as
 * ExplainPopover/STTLanguagePicker), so Escape and click-outside
 * dismissal come for free instead of a hand-rolled listener. */
import { useState } from 'react'
import { Badge, Button, Popover, PopoverContent, PopoverTrigger } from '@/components/ui'
import { useSetWorkflowVersionTag } from '@/hooks/useWorkflowVersions'
import type { WorkflowVersionRow } from '@/types/workflowVersions'

export function TagPopover({
  workflowId,
  row,
  allRows,
}: {
  workflowId: string
  row: WorkflowVersionRow
  allRows: WorkflowVersionRow[]
}) {
  const [open, setOpen] = useState(false)
  const [tag, setTag] = useState('')
  const setTagMutation = useSetWorkflowVersionTag(workflowId)
  const holder = allRows.find(
    (r) => r.version_number !== row.version_number && r.tags.includes(tag.trim()),
  )
  const existingTags = [...new Set(allRows.flatMap((r) => r.tags))].sort()

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o)
        // TagPopover no longer unmounts on close (Radix keeps the trigger
        // mounted), so reset the draft explicitly instead of relying on
        // unmount to discard it.
        if (!o) setTag('')
      }}
    >
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline">Tag</Button>
      </PopoverTrigger>
      <PopoverContent
        data-testid="tag-popover"
        align="start"
        sideOffset={4}
        className="w-64 space-y-2 p-3"
      >
        <input
          aria-label="Tag name"
          className="w-full rounded border border-border px-2 py-1 text-sm"
          placeholder="Tag name (e.g. canonical)"
          value={tag}
          maxLength={64}
          onChange={(e) => setTag(e.target.value)}
        />
        {existingTags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {existingTags.map((t) => (
              <button key={t} type="button" aria-pressed={tag === t} onClick={() => setTag(t)}>
                <Badge variant={tag === t ? 'default' : 'outline'}>{t}</Badge>
              </button>
            ))}
          </div>
        )}
        {holder && (
          <p className="text-xs text-warning-strong">
            '{tag.trim()}' is currently on v{holder.version_number} — applying moves it here.
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            size="sm"
            disabled={!tag.trim() || setTagMutation.isPending}
            isLoading={setTagMutation.isPending}
            onClick={() =>
              setTagMutation.mutate(
                { versionNumber: row.version_number, tag: tag.trim() },
                { onSuccess: () => setOpen(false) },
              )
            }
          >
            Apply tag
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
